import { useState, type FormEvent } from 'react';
import { SALES_STATUS_VALUES } from '@autodialer/shared';

import { ApiError } from '../../api/client';
import { contactsApi, type Campaign, type CampaignField, type Contact, type CampaignMember } from '../../api/campaigns';
import { Field, Notice } from '../../components/ui';
import { useI18n, type TranslationKey } from '../../i18n';
import { useSession } from '../../auth/session-context';

/**
 * Formular für einen Datensatz - zum Anlegen und Bearbeiten.
 *
 * Pflicht ist allein die Telefonnummer. Felder, die vor dem Gesprächsende
 * ausgefüllt sein müssen, sind gekennzeichnet; geprüft wird das erst mit der
 * Nachbearbeitung in einer späteren Phase.
 */
export function ContactForm({
  campaign,
  fields,
  members,
  contact,
  onSaved,
  onCancel,
}: {
  campaign: Campaign;
  fields: CampaignField[];
  members: CampaignMember[];
  contact: Contact | null;
  onSaved: () => void;
  onCancel: () => void;
}): JSX.Element {
  const { t, tError } = useI18n();
  const { principal, handleApiError } = useSession();

  const isAdmin = principal?.kind === 'tenant_user' && principal.isAdmin;

  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [firstName, setFirstName] = useState(contact?.firstName ?? '');
  const [lastName, setLastName] = useState(contact?.lastName ?? '');
  const [note, setNote] = useState(contact?.note ?? '');
  const [salesStatus, setSalesStatus] = useState(contact?.salesStatus ?? 'new');
  const [callbackDate, setCallbackDate] = useState(contact?.callbackDate ?? '');
  const [assignedUserId, setAssignedUserId] = useState(contact?.assignedUserId ?? '');
  const [values, setValues] = useState<Record<string, string>>(contact?.fields ?? {});
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void (async () => {
      setError(null);
      setBusy(true);
      try {
        const payload: Record<string, unknown> = {
          phone,
          firstName: firstName || null,
          lastName: lastName || null,
          note: note || null,
          salesStatus,
          callbackDate: callbackDate || null,
          fields: values,
        };
        if (isAdmin && campaign.campaignMode === 'assigned') {
          payload.assignedUserId = assignedUserId || null;
        }

        const response = contact
          ? await contactsApi.update(campaign.id, contact.id, payload)
          : await contactsApi.create(campaign.id, payload);

        if (response.contact.duplicateOf) {
          setDuplicate(response.contact.duplicateOf.publicId);
        }
        onSaved();
      } catch (caught) {
        handleApiError(caught);
        setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <form onSubmit={submit} className="card">
      <h2>{contact ? t('contacts.title') : t('contacts.create')}</h2>

      {contact ? (
        <p className="field-hint">
          {t('contact.id')}: {contact.publicId}
        </p>
      ) : null}
      {error ? <Notice tone="error">{error}</Notice> : null}
      {duplicate ? <Notice tone="info">{t('contact.duplicateHint', { id: duplicate })}</Notice> : null}

      <div className="form form-inline">
        <Field label={t('contact.phone')}>
          <input type="text" value={phone} onChange={(event) => setPhone(event.target.value)} />
        </Field>
        <Field label={t('contact.firstName')}>
          <input type="text" value={firstName} onChange={(event) => setFirstName(event.target.value)} />
        </Field>
        <Field label={t('contact.lastName')}>
          <input type="text" value={lastName} onChange={(event) => setLastName(event.target.value)} />
        </Field>
      </div>

      <div className="form form-inline">
        <Field label={t('contact.status')}>
          <select value={salesStatus} onChange={(event) => setSalesStatus(event.target.value as typeof salesStatus)}>
            {SALES_STATUS_VALUES.map((status) => (
              <option key={status} value={status}>
                {t(`status.${status}` as TranslationKey)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('contact.callbackDate')}>
          <input type="date" value={callbackDate} onChange={(event) => setCallbackDate(event.target.value)} />
        </Field>
        {isAdmin && campaign.campaignMode === 'assigned' ? (
          <Field label={t('contact.assignedTo')}>
            <select value={assignedUserId} onChange={(event) => setAssignedUserId(event.target.value)}>
              <option value="">{t('contact.unassigned')}</option>
              {members.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.username ?? member.email}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
      </div>

      <Field label={t('contact.note')}>
        <input type="text" value={note} onChange={(event) => setNote(event.target.value)} />
      </Field>

      {fields.length > 0 ? (
        <div className="form form-inline">
          {fields.map((field) => (
            <Field
              key={field.id}
              label={field.fieldName}
              hint={field.requiredBeforeCompletion ? t('contact.requiredBeforeCall') : undefined}
            >
              <input
                type="text"
                value={values[field.id] ?? ''}
                onChange={(event) => setValues({ ...values, [field.id]: event.target.value })}
              />
            </Field>
          ))}
        </div>
      ) : null}

      <div className="row-actions">
        <button type="submit" className="primary" disabled={busy}>
          {t('action.save')}
        </button>
        <button type="button" className="link-button" onClick={onCancel}>
          {t('action.cancel')}
        </button>
      </div>
    </form>
  );
}
